import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { CreditCard, Wallet, Loader2, Banknote } from "lucide-react";
import { toast } from "sonner";
import { api, eur, formatError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";

export default function Checkout() {
  const { user } = useAuth();
  const { items, total } = useCart();
  const [method, setMethod] = useState("stripe");
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ nome: "", indirizzo: "", citta: "", cap: "", telefono: "" });

  useEffect(() => {
    api.get("/settings").then((r) => {
      setSettings(r.data);
      if (!r.data.stripe_enabled && !r.data.paypal_enabled && r.data.cod_enabled) setMethod("cod");
      else if (!r.data.stripe_enabled && r.data.paypal_enabled) setMethod("paypal");
    }).catch(() => {});
  }, []);

  const shippingCost = settings
    ? method === "cod"
      ? settings.shipping_cost
      : settings.free_shipping_over > 0 && total >= settings.free_shipping_over
        ? 0
        : settings.shipping_cost
    : 0;
  const grandTotal = total + (settings ? shippingCost : 0);

  if (user === false) return <Navigate to="/accedi?redirect=/checkout" replace />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (!form.nome || !form.indirizzo || !form.citta || !form.cap) {
      toast.error("Compila tutti i campi di spedizione");
      return;
    }
    setConfirmOpen(true);
  };

  const processOrder = async () => {
    setConfirmOpen(false);
    setLoading(true);
    try {
      const { data } = await api.post("/checkout", {
        items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
        origin_url: window.location.origin,
        method,
        shipping: form,
      });
      if (data.cod) {
        window.location.href = "/pagamento/successo?metodo=contrassegno";
        return;
      }
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatError(err));
      setLoading(false);
    }
  };

  if (items.length === 0)
    return (
      <div data-testid="checkout-empty" className="max-w-3xl mx-auto px-6 py-24 text-center">
        <h1 className="font-display text-3xl uppercase tracking-tight">Il carrello è vuoto</h1>
        <Link to="/catalogo" className="btn-hard mt-6 inline-flex">Torna al catalogo</Link>
      </div>
    );

  return (
    <div data-testid="checkout-page" className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
      <div className="overline-tag">Ultimo passo</div>
      <h1 className="mt-3 font-display font-medium uppercase tracking-tight text-4xl md:text-5xl">Checkout</h1>

      <form onSubmit={submit} className="mt-10 flex flex-col gap-10">
        <section className="bg-white border border-[#e5e5e5] p-6 sm:p-8">
          <h2 className="font-display font-medium text-xl uppercase tracking-tight">Indirizzo di spedizione</h2>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input data-testid="shipping-name" value={form.nome} onChange={set("nome")} placeholder="Nome e cognome" className="rounded-none border-2 border-[#0a0a0a] sm:col-span-2" />
            <Input data-testid="shipping-address" value={form.indirizzo} onChange={set("indirizzo")} placeholder="Indirizzo e numero civico" className="rounded-none border-2 border-[#0a0a0a] sm:col-span-2" />
            <Input data-testid="shipping-city" value={form.citta} onChange={set("citta")} placeholder="Città" className="rounded-none border-2 border-[#0a0a0a]" />
            <Input data-testid="shipping-zip" value={form.cap} onChange={set("cap")} placeholder="CAP" className="rounded-none border-2 border-[#0a0a0a]" />
            <Input data-testid="shipping-phone" value={form.telefono} onChange={set("telefono")} placeholder="Telefono (opzionale)" className="rounded-none border-2 border-[#0a0a0a] sm:col-span-2" />
          </div>
        </section>

        <section className="bg-white border border-[#e5e5e5] p-6 sm:p-8">
          <h2 className="font-display font-medium text-xl uppercase tracking-tight">Metodo di pagamento</h2>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {(!settings || settings.stripe_enabled) && (
              <button
                type="button"
                data-testid="payment-method-stripe"
                onClick={() => setMethod("stripe")}
                className={`border-2 p-5 flex items-center gap-3 text-left transition-colors ${method === "stripe" ? "border-[#ff4500] bg-[#ff4500]/5" : "border-[#e5e5e5] hover:border-[#0a0a0a]"}`}
              >
                <CreditCard className="w-6 h-6 text-[#ff4500]" strokeWidth={1.5} />
                <span>
                  <span className="block font-semibold text-sm">Carta di credito</span>
                  <span className="block text-xs text-muted-foreground">Visa, Mastercard, Amex via Stripe</span>
                </span>
              </button>
            )}
            {settings?.paypal_enabled && (
              <button
                type="button"
                data-testid="payment-method-paypal"
                onClick={() => setMethod("paypal")}
                className={`border-2 p-5 flex items-center gap-3 text-left transition-colors ${method === "paypal" ? "border-[#ff4500] bg-[#ff4500]/5" : "border-[#e5e5e5] hover:border-[#0a0a0a]"}`}
              >
                <Wallet className="w-6 h-6 text-[#062e20]" strokeWidth={1.5} />
                <span>
                  <span className="block font-semibold text-sm">PayPal</span>
                  <span className="block text-xs text-muted-foreground">Paga con il tuo conto PayPal</span>
                </span>
              </button>
            )}
            {settings?.cod_enabled && (
              <button
                type="button"
                data-testid="payment-method-cod"
                onClick={() => setMethod("cod")}
                className={`border-2 p-5 flex items-center gap-3 text-left transition-colors ${method === "cod" ? "border-[#ff4500] bg-[#ff4500]/5" : "border-[#e5e5e5] hover:border-[#0a0a0a]"}`}
              >
                <Banknote className="w-6 h-6 text-[#062e20]" strokeWidth={1.5} />
                <span>
                  <span className="block font-semibold text-sm">Contrassegno</span>
                  <span className="block text-xs text-muted-foreground">
                    Paghi in contanti alla consegna · spedizione {eur(settings.shipping_cost)} sempre applicata
                  </span>
                </span>
              </button>
            )}
          </div>
          {method === "cod" && (
            <p data-testid="cod-notice" className="mt-4 text-sm bg-[#ffd600]/30 border border-[#ffd600] p-3">
              Con il contrassegno confermi l'ordine ora e paghi in contanti al corriere alla consegna. La spedizione di {settings ? eur(settings.shipping_cost) : "15,00 €"} è sempre applicata, anche sopra la soglia gratuita.
            </p>
          )}
        </section>

        <section className="bg-white border border-[#e5e5e5] p-6 sm:p-8">
          <h2 className="font-display font-medium text-xl uppercase tracking-tight">Il tuo ordine</h2>
          <div className="mt-5 flex flex-col gap-3">
            {items.map((i) => (
              <div key={i.id} className="flex justify-between text-sm">
                <span className="text-muted-foreground">{i.name} × {i.quantity}</span>
                <span className="font-mono">{eur(i.price * i.quantity)}</span>
              </div>
            ))}
            <div className="pt-4 mt-2 border-t border-[#e5e5e5] flex flex-col gap-2">
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>Subtotale</span>
                <span className="font-mono">{eur(total)}</span>
              </div>
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>Spedizione{settings?.shipping_note ? ` · ${settings.shipping_note}` : ""}</span>
                <span data-testid="checkout-shipping" className="font-mono">{shippingCost === 0 ? "Gratuita" : eur(shippingCost)}</span>
              </div>
              <div className="pt-3 mt-1 border-t border-[#e5e5e5] flex justify-between font-display font-semibold text-2xl">
                <span>Totale</span>
                <span data-testid="checkout-total" className="text-[#ff4500]">{eur(grandTotal)}</span>
              </div>
            </div>
          </div>
          <button data-testid="checkout-submit-button" disabled={loading} className="btn-hard w-full mt-8">
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> {method === "cod" ? "Conferma..." : "Reindirizzamento..."}
              </>
            ) : method === "cod" ? (
              <>Conferma ordine · paghi {eur(grandTotal)} alla consegna</>
            ) : (
              <>Paga ora {eur(grandTotal)}</>
            )}
          </button>
          <p className="mt-3 text-xs text-muted-foreground text-center">Verrai reindirizzato alla pagina di pagamento sicura.</p>
        </section>
      </form>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="order-confirm-dialog" className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-tight text-xl">Confermi l'acquisto?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-relaxed">
              Stai per effettuare un ordine di <strong className="text-[#ff4500]">{eur(grandTotal)}</strong>
              {method === "cod" ? (
                <> in <strong>contrassegno</strong>: pagherai in contanti al corriere alla consegna, spedizione inclusa.</>
              ) : (
                <>. Verrai reindirizzato al pagamento sicuro.</>
              )}
              <br />
              <br />
              Vuoi proseguire con l'acquisto?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="order-confirm-no" className="rounded-none">No, torna indietro</AlertDialogCancel>
            <AlertDialogAction data-testid="order-confirm-yes" onClick={processOrder} className="rounded-none bg-[#ff4500] hover:bg-[#e03e00]">
              Sì, confermo l'acquisto
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
